import { ChangeDetectionStrategy, Component, afterNextRender, computed, signal } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { Router } from '@angular/router';

import billingData from '../../../json/billing-management.mock.json';

type BillingWeek = { id: string; label: string };
type BillingRow = {
  qcaId: number | null;
  qcaName: string;
  payableAtPss: number;
  payableToPool: number;
  receivableFromPool: number;
  interPayableToPool: number;
  interReceivableFromPool: number;
  amount: number;
};
type BillingData = {
  financialYears: readonly string[];
  weekStartByFinancialYear: Record<string, string>;
  firstWeekEndByFinancialYear: Record<string, string>;
  billingRows: readonly BillingRow[];
};

@Component({
  selector: 'app-billing-management',
  imports: [CurrencyPipe],
  templateUrl: './billing-management.component.html',
  styleUrl: './billing-management.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingManagementComponent {
  readonly source = billingData as BillingData;
  readonly selectedFinancialYear = signal<string>('');
  readonly selectedWeek = signal<string>('');
  readonly appliedFinancialYear = signal<string>('');
  readonly appliedWeek = signal<string>('');
  readonly rows = signal<readonly BillingRow[]>(this.source.billingRows);
  readonly currentPage = signal(1);
  readonly pageSize = 10;

  readonly financialYears = this.source.financialYears;
  readonly weeks = computed(() => this.createWeeks(
    this.source.weekStartByFinancialYear[this.selectedFinancialYear()],
    this.source.firstWeekEndByFinancialYear[this.selectedFinancialYear()],
  ));
  readonly selectedWeekLabel = computed(
    () => this.weeks().find((week) => week.id === this.selectedWeek())?.label ?? '',
  );
  readonly appliedWeekLabel = computed(
    () => this.createWeeks(
      this.source.weekStartByFinancialYear[this.appliedFinancialYear()],
      this.source.firstWeekEndByFinancialYear[this.appliedFinancialYear()],
    ).find((week) => week.id === this.appliedWeek())?.label ?? '',
  );
  readonly billableRows = computed(() => this.rows().filter((row) => row.qcaId !== null));
  readonly totalQcas = computed(() => this.billableRows().length);
  readonly totalAmount = computed(() =>
    this.billableRows().reduce((total, row) => total + row.amount, 0),
  );
  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.billableRows().length / this.pageSize)),
  );
  readonly paginatedRows = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize;
    return this.billableRows().slice(start, start + this.pageSize);
  });
  readonly pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, index) => index + 1),
  );
  readonly showingText = computed(() => {
    const total = this.billableRows().length;
    const start = total ? (this.currentPage() - 1) * this.pageSize + 1 : 0;
    const end = Math.min(this.currentPage() * this.pageSize, total);
    return `Showing ${start} to ${end} of ${total} entries`;
  });

  constructor(private readonly router: Router) {
    afterNextRender(() => this.applyCurrentWeekSelection());
  }

  changeFinancialYear(financialYear: string): void {
    this.selectedFinancialYear.set(financialYear);
    this.selectedWeek.set(this.source.weekStartByFinancialYear[financialYear] ?? '');
  }

  search(): void {
    this.appliedFinancialYear.set(this.selectedFinancialYear());
    this.appliedWeek.set(this.selectedWeek());
    this.currentPage.set(1);
  }

  reset(): void {
    this.applyCurrentWeekSelection();
    this.currentPage.set(1);
  }

  goToPage(page: number): void {
    this.currentPage.set(Math.min(Math.max(page, 1), this.totalPages()));
  }

  selectCompany(row: BillingRow): void {
    if (row.qcaId === null) return;
    void this.router.navigate(['/billing-management/qca', row.qcaId], {
      queryParams: {
        financialYear: this.selectedFinancialYear(),
        week: this.selectedWeek(),
      },
    });
  }

  rowTotal(row: BillingRow): number {
    return row.amount;
  }

  private createWeeks(firstWeekStart: string | undefined, firstWeekEnd?: string): BillingWeek[] {
    if (!firstWeekStart) return [];

    const weeks: BillingWeek[] = [];
    let start = this.parseDate(firstWeekStart);
    let end = firstWeekEnd ? this.parseDate(firstWeekEnd) : this.addDays(start, 6);

    for (let weekNumber = 1; weekNumber <= 52; weekNumber += 1) {
      weeks.push({
        id: this.toIsoDate(start),
        label: `Week ${weekNumber}: ${this.formatDate(start)} - ${this.formatDate(end)}`,
      });
      start = this.addDays(end, 1);
      end = this.addDays(start, 6);
    }

    return weeks;
  }

  private getCurrentWeekSelection(): { financialYear: string; week: string } {
    const fallbackFinancialYear = this.source.financialYears[0];
    const fallbackWeek = this.source.weekStartByFinancialYear[fallbackFinancialYear] ?? '';
    const today = this.toIsoDate(new Date());
    let currentSelection: { financialYear: string; week: string } | undefined;

    for (const financialYear of this.source.financialYears) {
      const weeks = this.createWeeks(
        this.source.weekStartByFinancialYear[financialYear],
        this.source.firstWeekEndByFinancialYear[financialYear],
      );
      const currentWeek = [...weeks].reverse().find((week) => week.id <= today);

      if (currentWeek) {
        currentSelection = { financialYear, week: currentWeek.id };
      }
    }

    if (currentSelection) return currentSelection;

    const fallbackCurrentDate = `${fallbackWeek.slice(0, 4)}-${today.slice(5)}`;
    const fallbackCurrentWeek = this.createWeeks(
      fallbackWeek,
      this.source.firstWeekEndByFinancialYear[fallbackFinancialYear],
    ).reverse().find((week) => week.id <= fallbackCurrentDate);

    return {
      financialYear: fallbackFinancialYear,
      week: fallbackCurrentWeek?.id ?? fallbackWeek,
    };
  }

  private applyCurrentWeekSelection(): void {
    const currentWeekSelection = this.getCurrentWeekSelection();
    this.selectedFinancialYear.set(currentWeekSelection.financialYear);
    this.selectedWeek.set(currentWeekSelection.week);

    console.log('Current Week Selection:', currentWeekSelection);
    this.appliedFinancialYear.set(currentWeekSelection.financialYear);
    this.appliedWeek.set(currentWeekSelection.week);
  }

  private parseDate(value: string): Date {
    return new Date(`${value}T00:00:00Z`);
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() + days);
    return result;
  }

  private toIsoDate(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  private formatDate(date: Date): string {
    return `${String(date.getUTCDate()).padStart(2, '0')}/${String(date.getUTCMonth() + 1).padStart(2, '0')}/${String(date.getUTCFullYear()).slice(-2)}`;
  }
}
